import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  CircularProgress,
  Paper,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Link,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { crawlReddit, summarizeText, getLLMModels, saveRedditPost, crawlSubreddit, googleSearch } from '../utils/api';
import SavedPostsList from '../components/SavedPostsList';
import RedditJobManagement from '../components/RedditJobManagement';
import GoogleSearchWidget from '../components/GoogleSearchWidget';

interface LLMModel {
  id: number;
  model_id: string;
  model_caption: string;
  is_default: boolean;
}

const RedditCrawlerPage = () => {
  const [url, setUrl] = useState('');
  const [subreddit, setSubreddit] = useState('');
  const [postLimit, setPostLimit] = useState(100);
  const [appId, setAppId] = useState(1);
  const [content, setContent] = useState<any>(null);
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [crawlingSubreddit, setCrawlingSubreddit] = useState(false);
  const [error, setError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [mainTabValue, setMainTabValue] = useState(0);
  const [crawlerTabValue, setCrawlerTabValue] = useState(0);
  const [contentTabValue, setContentTabValue] = useState(0);
  const [models, setModels] = useState<LLMModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<number | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any>(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await getLLMModels();
        if (!response.ok) {
          throw new Error('Failed to fetch LLM models.');
        }
        const data = await response.json();
        const modelsList = data.results || data; // Handle paginated response
        setModels(modelsList);
        const defaultModel = modelsList.find((m: LLMModel) => m.is_default);
        if (defaultModel) {
          setSelectedModel(defaultModel.id);
        } else if (modelsList.length > 0) {
          setSelectedModel(modelsList[0].id);
        }
      } catch (err: any) {
        setError(err.message);
      }
    };
    fetchModels();
  }, []);

  const handleSearch = async () => {
    if (!searchQuery) {
      setError('Please enter a search query.');
      return;
    }
    setError('');
    setSearching(true);
    setSearchResults(null);

    try {
      const response = await googleSearch(searchQuery);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Search failed');
      }
      const data = await response.json();
      console.log('Search results:', data);
      setSearchResults(data);
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred during search.');
    } finally {
      setSearching(false);
    }
  };

  const handleCrawl = async () => {
    if (!url) {
      setError('Please enter a Reddit URL.');
      return;
    }
    setError('');
    setLoading(true);
    setContent(null);
    setSummary('');
    setSaveMessage('');

    try {
      const response = await crawlReddit(url, appId);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Crawl failed');
      }
      const data = await response.json();
      setContent(data);
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const handleSummarize = async () => {
    if (!content) {
      setError('Please crawl content first.');
      return;
    }
    setError('');
    setSummarizing(true);
    setSummary('');

    try {
      const textToSummarize = JSON.stringify(content, null, 2);
      const modelId = selectedModel ? Number(selectedModel) : undefined;
      const response = await summarizeText(textToSummarize, modelId);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Summarization failed');
      }
      const data = await response.json();
      setSummary(data.summary);
      setContentTabValue(1);
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred during summarization.');
    } finally {
      setSummarizing(false);
    }
  };

  const handleSaveToDB = async () => {
    if (!content) {
      setError('Please crawl content first.');
      return;
    }
    setError('');
    setSaveMessage('');

    try {
      const postData = {
        url: content.url,
        title: content.title,
        created_time: new Date(content.created_utc * 1000).toISOString(),
        json_data: JSON.stringify(content),
      };

      const response = await saveRedditPost(postData);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to save post.');
      }
      
      setSaveMessage(data.message);

    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred during save.');
    }
  };

  const handleSubredditCrawl = async () => {
    if (!subreddit) {
      setError('Please enter a subreddit name.');
      return;
    }
    setError('');
    setSaveMessage('');
    setCrawlingSubreddit(true);
    
    try {
      const response = await crawlSubreddit({
        subreddit,
        limit: postLimit,
        app_id: appId,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Subreddit crawl failed');
      }
      
      setSaveMessage(data.message);

    } catch (err: any) {
      setError(err.message);
    } finally {
      setCrawlingSubreddit(false);
    }
  };

  const handleMainTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setMainTabValue(newValue);
  };

  const handleCrawlerTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCrawlerTabValue(newValue);
  };

  const handleContentTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setContentTabValue(newValue);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Reddit Tool
        </Typography>

        <Tabs value={mainTabValue} onChange={handleMainTabChange} aria-label="main tabs">
          <Tab label="Crawler" />
          <Tab label="Saved Posts" />
          <Tab label="Search" />
        </Tabs>

        {mainTabValue === 0 && (
          <Box sx={{ pt: 3 }}>
            <Tabs value={crawlerTabValue} onChange={handleCrawlerTabChange} aria-label="crawler type tabs">
              <Tab label="Crawl Single Post" />
              <Tab label="Crawl Subreddit" />
              <Tab label="Crawler Job" />
            </Tabs>

            {crawlerTabValue === 0 && (
              <Box sx={{ pt: 3 }}>
                <Typography paragraph>
                  Enter a Reddit post URL, fetch its content, and then perform actions like summarizing or saving.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Reddit Post URL"
                    variant="outlined"
                    value={url}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)}
                    disabled={loading || summarizing}
                  />
                  <TextField
                    type="number"
                    label="App ID"
                    variant="outlined"
                    value={appId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAppId(parseInt(e.target.value, 10))}
                    disabled={loading || summarizing}
                    sx={{ width: '150px' }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCrawl}
                    disabled={loading || summarizing}
                    sx={{ height: '56px' }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Go'}
                  </Button>
                </Box>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {saveMessage && <Alert severity="info" sx={{ mb: 2 }}>{saveMessage}</Alert>}
                {content && (
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <FormControl sx={{ minWidth: 240 }}>
                        <InputLabel id="llm-model-select-label">LLM Model</InputLabel>
                        <Select
                          labelId="llm-model-select-label"
                          id="llm-model-select"
                          value={selectedModel}
                          label="LLM Model"
                          onChange={(e) => setSelectedModel(e.target.value as number)}
                          disabled={summarizing || models.length === 0}
                        >
                          {models.map((model) => (
                            <MenuItem key={model.id} value={model.id}>
                              {model.model_caption}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleSummarize}
                        disabled={summarizing || !content}
                      >
                        {summarizing ? <CircularProgress size={24} color="inherit" /> : 'Summarize'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleSaveToDB}
                        disabled={!content}
                      >
                        Save to DB
                      </Button>
                    </Box>
                    <Paper elevation={2} sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
                      <Tabs value={contentTabValue} onChange={handleContentTabChange} aria-label="content tabs">
                        <Tab label="Raw Content" />
                        <Tab label="Summary" disabled={!summary} />
                      </Tabs>
                      <Box sx={{ pt: 2 }}>
                        {contentTabValue === 0 && (
                          <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {JSON.stringify(content, null, 2)}
                          </Box>
                        )}
                        {contentTabValue === 1 && (
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {summary}
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  </Box>
                )}
              </Box>
            )}

            {crawlerTabValue === 1 && (
              <Box sx={{ pt: 3 }}>
                <Typography paragraph>
                  Enter a subreddit name, specify the number of posts to fetch, and save them directly to the database.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Subreddit Name (e.g., dataengineering)"
                    variant="outlined"
                    value={subreddit}
                    onChange={(e) => setSubreddit(e.target.value)}
                    disabled={crawlingSubreddit}
                  />
                  <TextField
                    type="number"
                    label="Number of posts"
                    variant="outlined"
                    value={postLimit}
                    onChange={(e) => setPostLimit(Math.max(1, Math.min(1000, parseInt(e.target.value, 10))))}
                    disabled={crawlingSubreddit}
                    sx={{ width: '200px' }}
                    inputProps={{ min: 1, max: 1000 }}
                  />
                  <TextField
                    type="number"
                    label="App ID"
                    variant="outlined"
                    value={appId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAppId(parseInt(e.target.value, 10))}
                    disabled={crawlingSubreddit}
                    sx={{ width: '150px' }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubredditCrawl}
                    disabled={crawlingSubreddit}
                    sx={{ height: '56px' }}
                  >
                    {crawlingSubreddit ? <CircularProgress size={24} /> : 'Crawl & Save'}
                  </Button>
                </Box>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {saveMessage && <Alert severity="info" sx={{ mb: 2 }}>{saveMessage}</Alert>}
              </Box>
            )}

            {crawlerTabValue === 2 && (
              <Box sx={{ pt: 3 }}>
                <RedditJobManagement />
              </Box>
            )}
          </Box>
        )}
        {mainTabValue === 1 && (
          <Box sx={{ pt: 3 }}>
            <SavedPostsList />
          </Box>
        )}

        {mainTabValue === 2 && (
          <Box sx={{ pt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Google Cloud Search
            </Typography>
            <Typography paragraph>
              Use the search widget below to query documents from the connected data store.
            </Typography>
            <GoogleSearchWidget />

            <Box sx={{ my: 4 }}>
              <Typography variant="h6" gutterBottom>
                Search via API
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Search Query"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  variant="outlined"
                />
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={searching}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {searching ? <CircularProgress size={24} /> : 'Search through API'}
                </Button>
              </Box>

              {error && <Alert severity="error">{error}</Alert>}

              {searchResults && (
                <Card>
                  <CardContent>
                    {searchResults.answer?.answer && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6">Generated Answer</Typography>
                        <Typography
                          component="div"
                          dangerouslySetInnerHTML={{
                            __html: searchResults.answer.answer.replace(/\n/g, '<br />'),
                          }}
                        />
                      </Box>
                    )}
                    <Typography variant="h6">Search Results</Typography>
                    <List>
                      {searchResults.results?.map((item: any) => (
                        <ListItem key={item.id}>
                          <ListItemText
                            primary={<Link href={item.document.derivedStructData.link} target="_blank">{item.document.derivedStructData.title}</Link>}
                            secondary={item.document.derivedStructData.snippets?.[0]?.snippet}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default RedditCrawlerPage; 